import React, { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useSearchParams } from 'react-router-dom'
import { EnhancedMealPlanEditor } from '@/components/EnhancedMealPlanEditor'
import { MealOptionManager } from '@/components/MealOptionManager'
import { ParentMcDonaldsOrderView } from '@/components/ParentMcDonaldsOrderView'
import { KidMealPlanner } from '@/components/KidMealPlanner'
import { ChildFoodOrdering } from '@/components/ChildFoodOrdering'
import { ParentFoodOrderApproval } from '@/components/ParentFoodOrderApproval'
import { ParentVendorManagement } from '@/components/ParentVendorManagement'
import { ParentMenuStatistics } from '@/components/ParentMenuStatistics'
import RecipeList from '@/components/recipes/RecipeList'
import { RecipeImporter } from '@/components/recipes/RecipeImporter'
import { ShoppingList } from '@/components/ShoppingList'
import { getVendors } from '@/api/foodOrders'
import { UtensilsCrossed, ShoppingBag, BookOpen, Store, ClipboardCheck, Settings, Calendar, ListPlus } from 'lucide-react'

type TabType = 'meals' | 'ordering' | 'recipes' | 'shopping'
type OrderingSubTab = 'place' | 'approvals' | 'vendors'
type MealsSubTab = 'plan' | 'manage'

/**
 * Meals page - Shows meal planning, recipes, and shopping list
 * Route: /dashboard/meals
 */
export const MealsPage: React.FC = () => {
  const { user } = useAuth()
  const [searchParams] = useSearchParams()
  const urlTab = (searchParams.get('tab') as TabType) || 'meals'
  const [activeTab, setActiveTab] = useState<TabType>(urlTab)
  const [childView, setChildView] = useState<'planner' | 'ordering'>('planner')
  const [recipeSubTab, setRecipeSubTab] = useState<'list' | 'import'>('list')
  const [orderingSubTab, setOrderingSubTab] = useState<OrderingSubTab>('place')
  const [mealsSubTab, setMealsSubTab] = useState<MealsSubTab>('plan')
  const [hasVendors, setHasVendors] = useState(false)
  const [triggerCreateMeal, setTriggerCreateMeal] = useState(false)

  // Handler for "Add New Meal" from the meal planner
  const handleAddNewMeal = () => {
    setTriggerCreateMeal(true)
    setMealsSubTab('manage')
  }

  // Handler for "Manage Meals" from the settings gear
  const handleManageMeals = () => {
    setMealsSubTab('manage')
  }

  // Reset trigger after it's consumed
  useEffect(() => {
    if (triggerCreateMeal && mealsSubTab === 'manage') {
      // Reset after a short delay so MealOptionManager can consume it
      const timer = setTimeout(() => setTriggerCreateMeal(false), 100)
      return () => clearTimeout(timer)
    }
  }, [triggerCreateMeal, mealsSubTab])

  // Check if there are any active vendors
  useEffect(() => {
    const checkVendors = async () => {
      try {
        const vendors = await getVendors(true)
        setHasVendors(Array.isArray(vendors) && vendors.length > 0)
      } catch (error) {
        console.error('Error checking vendors:', error)
        setHasVendors(false)
      }
    }
    checkVendors()
  }, [])

  // Sync activeTab with URL params (tabs are changed via DashboardLayout header)
  useEffect(() => {
    if (urlTab !== activeTab) {
      setActiveTab(urlTab)
    }
  }, [urlTab])

  // Kids view
  if (!user?.is_parent) {
    return (
      <div className="space-y-6">
        {/* Tab switcher for children - Meal Planning first, Order Food only if vendors exist */}
        <div className="flex gap-2 bg-white p-1 rounded-lg border border-gray-200 w-fit">
          <button
            onClick={() => setChildView('planner')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-colors ${
              childView === 'planner'
                ? 'bg-orange-600 text-white'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <UtensilsCrossed className="w-4 h-4" />
            Meal Planning
          </button>
          {hasVendors && (
            <button
              onClick={() => setChildView('ordering')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-colors ${
                childView === 'ordering'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <ShoppingBag className="w-4 h-4" />
              Order Food
            </button>
          )}
        </div>

        {childView === 'ordering' && hasVendors ? <ChildFoodOrdering /> : <KidMealPlanner />}
      </div>
    )
  }

  // Parent view - tabs are in the header bar via DashboardLayout
  return (
    <div className="space-y-6">
      {/* Tab Content */}
      {activeTab === 'meals' && (
        <div className="space-y-6">
          {/* Sub-tab switcher */}
          <div className="flex gap-2 bg-white p-1 rounded-lg border border-gray-200 w-fit">
            <button
              onClick={() => setMealsSubTab('plan')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-colors ${
                mealsSubTab === 'plan'
                  ? 'bg-orange-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Calendar className="w-4 h-4" />
              Weekly Plan
            </button>
            <button
              onClick={() => setMealsSubTab('manage')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-colors ${
                mealsSubTab === 'manage'
                  ? 'bg-orange-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <ListPlus className="w-4 h-4" />
              Manage Meals
            </button>
          </div>

          {mealsSubTab === 'plan' && <EnhancedMealPlanEditor onAddNewMeal={handleAddNewMeal} onManageMeals={handleManageMeals} />}
          {mealsSubTab === 'manage' && <MealOptionManager isParent={true} startCreating={triggerCreateMeal} />}
        </div>
      )}

      {activeTab === 'ordering' && (
        <div className="space-y-6">
          {/* Sub-tab switcher */}
          <div className="flex gap-2 bg-white p-1 rounded-lg border border-gray-200 w-fit">
            <button
              onClick={() => setOrderingSubTab('place')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-colors ${
                orderingSubTab === 'place'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <ShoppingBag className="w-4 h-4" />
              Place Order
            </button>
            <button
              onClick={() => setOrderingSubTab('approvals')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-colors ${
                orderingSubTab === 'approvals'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <ClipboardCheck className="w-4 h-4" />
              Approvals
            </button>
            <button
              onClick={() => setOrderingSubTab('vendors')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-colors ${
                orderingSubTab === 'vendors'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Settings className="w-4 h-4" />
              Manage Vendors
            </button>
          </div>

          {orderingSubTab === 'place' && <ChildFoodOrdering />}

          {orderingSubTab === 'approvals' && (
            <div className="space-y-6">
              <ParentFoodOrderApproval />
              <ParentMcDonaldsOrderView />
            </div>
          )}

          {orderingSubTab === 'vendors' && (
            <div className="space-y-6">
              <ParentVendorManagement />
              <ParentMenuStatistics />
            </div>
          )}
        </div>
      )}

      {activeTab === 'recipes' && (
        <div className="space-y-6">
          {/* Sub-tab switcher */}
          <div className="flex gap-2 bg-white p-1 rounded-lg border border-gray-200 w-fit">
            <button
              onClick={() => setRecipeSubTab('list')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-colors ${
                recipeSubTab === 'list'
                  ? 'bg-pink-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <BookOpen className="w-4 h-4" />
              My Recipes
            </button>
            <button
              onClick={() => setRecipeSubTab('import')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-colors ${
                recipeSubTab === 'import'
                  ? 'bg-pink-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <ShoppingBag className="w-4 h-4" />
              Import Recipe
            </button>
          </div>

          {recipeSubTab === 'list' ? (
            <RecipeList />
          ) : (
            <RecipeImporter onImportSuccess={() => setRecipeSubTab('list')} />
          )}
        </div>
      )}

      {activeTab === 'shopping' && <ShoppingList />}
    </div>
  )
}
